import React, { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';

import EntityFormPage, { EntityForm } from '@entity/EntityForm/EntityForm';
import getGraphqlData from '@boilerplate/lib/getGraphqlData';
import { BaseFormUpdateProps } from '@boilerplate/types/entity';
import { addEntityToRelatedDataOptions } from '@boilerplate/lib/relatedDataToOptions';
import Notistack from '@/lib/notistack';
import TenantEntity from '@/entities/tenant';
import { catchGraphQlError } from '@/lib/catchError';

  import UserTenantRoleEntity from '@/entities/userTenantRole';
  import ProjectEntity from '@/entities/project';
  import BudgetLineEntity from '@/entities/budgetLine';
  import LoggedHourEntity from '@/entities/loggedHour';
  import ContractEntity from '@/entities/contract';
  import InvoiceEntity from '@/entities/invoice';

function TenantBaseUpdateForm({ formOnly = false, onClose, Entity = TenantEntity, id: propId, ...props }: BaseFormUpdateProps<typeof TenantEntity.model>) {
  const { id: paramId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const id = propId || paramId;

  const Component = formOnly ? EntityForm : EntityFormPage;

  const { loading, data } = TenantEntity.model.useGet({
    variables: {
      id,
            withProjects: true,
    },
  });

  const [updateMutation, { loading: updateLoading }] = TenantEntity.model.useUpdate();

  const relationsOptionsQuery = TenantEntity.model.useRelationsOptions({
    variables: {
          withProjects: true,
    }
  });

  const relationsOptions = useMemo(
    () =>
      addEntityToRelatedDataOptions(relationsOptionsQuery, [
              ProjectEntity,
      ]),
    [relationsOptionsQuery]
  );

  const handleSubmit = (newData) => {
    newData.variables = getGraphqlData(Entity, newData.variables, 'update');

    updateMutation(newData)
      .then((result) => {
        Notistack.toast(t('crud:updatedItem', { item: t('entities:tenant') }), { variant: 'success' });

        if (!formOnly) {
          navigate(-1);
        }

        return onClose?.(result.data?.updateTenant);
      })
      .catch(catchGraphQlError);
  };

  if (loading || relationsOptionsQuery.loading) {
    return <CircularProgress />;
  }

  return (
    <Component
      {...props}
      type="update"
      Entity={Entity}
      loading={updateLoading}
      handleSubmit={handleSubmit}
      relationsOptions={relationsOptions}
      data={data.tenant}
    />
  );
}

export default TenantBaseUpdateForm;
